import React, { useState, useEffect, useRef } from 'react';
import { Upload, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { uploadImageCDN } from '../utils/utility';

/**
 * UploadButton component - upload ảnh lên CDN (picture-card style).
 * Tương thích Form.Item qua value/onChange.
 *
 * Props:
 * - value: string | string[] (URL hiện tại, từ Form.Item)
 * - onChange: function (callback từ Form.Item)
 * - multiple: boolean (cho phép upload nhiều ảnh, mặc định false)
 * - maxCount: number (số ảnh tối đa, mặc định 1)
 */
const UploadButton = React.forwardRef(({ value, onChange, multiple = false, maxCount = 1, uploadFolder = 'lichviet', ...rest }, ref) => {
  const [fileList, setFileList] = useState([]);
  const isUploadingRef = useRef(false); // tránh useEffect reset khi đang upload

  // Sync value từ Form vào fileList (chỉ khi không đang upload)
  useEffect(() => {
    if (isUploadingRef.current) return; // đang upload, không override
    if (!value) {
      setFileList([]);
      return;
    }
    const urls = Array.isArray(value) ? value : [value];
    setFileList(
      urls.map((url, i) => ({
        uid: String(-1 - i),
        name: url.split('/').pop() || `image_${i}`,
        status: 'done',
        url,
      }))
    );
  }, [value]);

  const handleChange = ({ fileList: newFileList }) => {
    // Normalize: gán url từ response nếu chưa có (antd không tự populate file.url)
    const normalized = newFileList.map((file) => ({
      ...file,
      url: file.url || file.response?.url,
    }));
    setFileList(normalized);

    // Chỉ emit URL khi tất cả file đã done (không emit khi đang uploading)
    const hasUploading = normalized.some((f) => f.status === 'uploading');
    if (hasUploading) return;

    const doneUrls = normalized.filter((f) => f.status === 'done' && f.url).map((f) => f.url);
    if (!multiple || maxCount === 1) {
      onChange?.(doneUrls[0] ?? undefined);
    } else {
      onChange?.(doneUrls.length > 0 ? doneUrls : undefined);
    }
  };

  const customRequest = async ({ file, onSuccess, onError, onProgress }) => {
    isUploadingRef.current = true;
    try {
      onProgress?.({ percent: 50 });
      const url = await uploadImageCDN(file, () => {}, uploadFolder);
      if (url) {
        onSuccess?.({ url });
      } else {
        onError?.(new Error('Upload thất bại'));
      }
    } catch (err) {
      onError?.(err);
      message.error('Lỗi khi upload ảnh!');
    } finally {
      isUploadingRef.current = false;
    }
  };

  return (
    <Upload
      ref={ref}
      listType="picture-card"
      fileList={fileList}
      customRequest={customRequest}
      onChange={handleChange}
      accept="image/*"
      multiple={multiple}
      maxCount={maxCount}
      showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
      {...rest}
    >
      {fileList.length < maxCount && (
        <button type="button" style={{ border: 0, background: 'none', cursor: 'pointer' }}>
          <PlusOutlined />
          <div style={{ marginTop: 8, fontSize: 12 }}>Tải ảnh</div>
        </button>
      )}
    </Upload>
  );
});

UploadButton.displayName = 'UploadButton';

export default UploadButton;
